/**
 * パスワード生成器の履歴 (in-memory only)。
 *
 * セキュリティ要件:
 *   - 生成した値は永続化しない (localStorage / IndexedDB / Cookie に絶対書かない)
 *   - タブを閉じれば消える (module スコープのため tab close で破棄)
 *   - 容量上限を設けて古いものから破棄 (MAX_HISTORY)
 *   - パスワードや passphrase 本体を log / sentry に出さない
 *
 * UI の都合上 GeneratorPage タブ切替で state を保持したいため、React state ではなく
 * module-level store で持つ。`subscribe` 経由で useSyncExternalStore から購読する。
 */

const MAX_HISTORY = 20

export type GeneratorHistoryKind = 'password' | 'passphrase' | 'username'

export interface GeneratorHistoryEntry {
  id: string
  kind: GeneratorHistoryKind
  value: string
  createdAt: number
}

type Listener = () => void

let entries: readonly GeneratorHistoryEntry[] = []
const listeners = new Set<Listener>()

const emit = (): void => {
  for (const l of listeners) l()
}

const nextId = (): string => `gen-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

export const generatorHistoryStore = {
  getEntries(): readonly GeneratorHistoryEntry[] {
    return entries
  },

  push(kind: GeneratorHistoryKind, value: string): void {
    if (!value) return
    // 直前と同一値は重複追加しない (再生成連打で履歴が埋まるのを防ぐ)
    if (entries[0] && entries[0].value === value && entries[0].kind === kind) return

    const entry: GeneratorHistoryEntry = {
      id: nextId(),
      kind,
      value,
      createdAt: Date.now(),
    }

    entries = [entry, ...entries].slice(0, MAX_HISTORY)
    emit()
  },

  remove(id: string): void {
    entries = entries.filter(e => e.id !== id)
    emit()
  },

  clear(): void {
    entries = []
    emit()
  },

  subscribe(cb: Listener): () => void {
    listeners.add(cb)

    return () => {
      listeners.delete(cb)
    }
  },
}
